import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { prepareSearchParams, updateUrl } from 'utils/url';

import { TableLayout } from '@templates/TableLayout/TableLayout';
import type {
  CollectionPaymentFindOne,
  Movement,
  Order,
  PayoutRequestItem,
  WithdrawalRequests
} from 'api';

import { useGlobalFilters } from 'hooks/useGlobalFilters';

import { searchStateToParams, useLocalState } from './localState';
import { NoSearchResult } from './NoSearchResult';
import { OrdersSearchResults } from './OrdersSearchResults';
import { PaymentsSearchResults } from './PaymentsSearchResults';
import { PayoutsSearchResults } from './PayoutsSearchResults';
import { SearchHeader } from './SearchHeader';
import { TransactionsSearchResults } from './TransactionsSearchResult';
import { WithdrawalsSearchResults } from './WithdrawalsSearchResults';

export const Search = () => {
  const {
    localState: { q },
    queryParams
  } = useLocalState();

  const navigate = useNavigate();
  const { '*': childRoute } = useParams();
  const { filters, queryParams: globalQueryParams } = useGlobalFilters();

  const [statuses, setStatuses] = useState<{
    [key: string]: { loaded: boolean; id?: string };
  }>({
    payouts: {
      loaded: false,
      id: undefined
    },
    transactions: {
      loaded: false,
      id: undefined
    },
    orders: {
      loaded: false,
      id: undefined
    },
    payments: {
      loaded: false,
      id: undefined
    },
    withdrawals: {
      loaded: false,
      id: undefined
    }
  });

  const onPayoutSuccess = useCallback(
    (payout: PayoutRequestItem | undefined) => {
      setStatuses((statuses) => ({
        ...statuses,
        payouts: {
          loaded: true,
          id: payout?.unique_reference
        }
      }));
    },
    []
  );

  const onTransactionSuccess = useCallback(
    (transaction: Movement | undefined) => {
      setStatuses((statuses) => ({
        ...statuses,
        transactions: {
          loaded: true,
          id: transaction?.reference
        }
      }));
    },
    []
  );

  const onOrderSuccess = useCallback((order: Order | undefined) => {
    setStatuses((statuses) => ({
      ...statuses,
      orders: {
        loaded: true,
        id: order?.uuid
      }
    }));
  }, []);

  const onPaymentSuccess = useCallback(
    (payment: CollectionPaymentFindOne | undefined) => {
      setStatuses((statuses) => ({
        ...statuses,
        payments: {
          loaded: true,
          id: payment?.uuid
        }
      }));
    },
    []
  );

  const onWithdrawalSuccess = useCallback(
    (withdrawal: WithdrawalRequests | undefined) => {
      setStatuses((statuses) => ({
        ...statuses,
        withdrawals: {
          loaded: true,
          id: withdrawal?.reference
        }
      }));
    },
    []
  );

  const searchParams = useMemo(
    () => (q ? searchStateToParams({ q, ...filters }) : skipToken),
    [q, filters]
  );

  useEffect(() => {
    if (!childRoute) {
      updateUrl(
        `${location.pathname}?${prepareSearchParams(location.search, {
          ...queryParams,
          ...globalQueryParams
        }).toString()}`
      );
    }
  }, [queryParams, globalQueryParams, childRoute]);

  useEffect(() => {
    if (statuses.transactions.id) {
      navigate(
        `transactions/${statuses.transactions.id}?${prepareSearchParams(
          location.search,
          queryParams
        )}`
      );
    } else if (statuses.payouts.id) {
      navigate(
        `payouts/${statuses.payouts.id}?${prepareSearchParams(
          location.search,
          queryParams
        )}`
      );
    } else if (statuses.orders.id) {
      navigate(
        `orders/${statuses.orders.id}?${prepareSearchParams(
          location.search,
          queryParams
        )}`
      );
    } else if (statuses.payments.id) {
      navigate(
        `payments/${statuses.payments.id}?${prepareSearchParams(
          location.search,
          queryParams
        )}`
      );
    } else if (statuses.withdrawals.id) {
      navigate(
        `withdrawals/${statuses.withdrawals.id}?${prepareSearchParams(
          location.search,
          queryParams
        )}`
      );
    }
  }, [navigate, queryParams, statuses]);

  useEffect(() => {
    setStatuses({
      payouts: {
        loaded: false,
        id: undefined
      },
      transactions: {
        loaded: false,
        id: undefined
      },
      orders: {
        loaded: false,
        id: undefined
      },
      payments: {
        loaded: false,
        id: undefined
      },
      withdrawals: {
        loaded: false,
        id: undefined
      }
    });
  }, [q, filters]);

  const statusEntries = Object.entries(statuses);
  const allSearchesFinished = statusEntries.every(
    ([_key, { loaded }]) => loaded
  );
  const noResults = !statusEntries.some(([_key, { id }]) => !!id);

  if (!q) return <NoSearchResult />;
  if (allSearchesFinished && noResults) return <NoSearchResult />;

  return (
    <TableLayout
      header={<SearchHeader />}
      content={
        <section aria-label="search results" className="flex flex-col gap-4">
          <TransactionsSearchResults
            searchParams={searchParams}
            onSuccess={onTransactionSuccess}
          />
          <PayoutsSearchResults
            searchParams={searchParams}
            onSuccess={onPayoutSuccess}
          />
          <OrdersSearchResults
            searchParams={searchParams}
            onSuccess={onOrderSuccess}
          />
          <PaymentsSearchResults
            searchParams={searchParams}
            onSuccess={onPaymentSuccess}
          />
          <WithdrawalsSearchResults
            searchParams={searchParams}
            onSuccess={onWithdrawalSuccess}
          />
        </section>
      }
    />
  );
};
